import React, { FC, useMemo } from 'react';
import { formatUnits } from 'ethers';
import { copyToClipboard, truncate } from '@blink/components/src/utils';
import { ReactComponent as CopyIcon } from '@blink/assets/src/icons/copy.svg';
import { ReactComponent as QuestionIcon } from '@blink/assets/src/icons/question.svg';
import { Button } from '@blink/components';
import { Tooltip } from 'antd';
import SkeletonInput from 'antd/es/skeleton/Input';
import { useSelector } from 'react-redux';

import { useBorrowDetailsSummarySectionStyle } from './BorrowDetailsSummarySection.style';
import { RiskFactor } from '../../../RiskFactor/RiskFactor';
import { RiskFactorBar } from 'src/components/RiskFactor/RiskFactorBar';
import { RiskFactorTooltip } from 'src/components/RiskFactor/RiskFactorTooltip';

import { Store } from '@/shared/store';
import { History, MaintenanceData, UserBorrow } from '@/shared/api/borrow';
import { toast } from '@/shared/hooks/useToast';
import { formatWithCommas } from '@/shared/utils/helpers';
import { AssetIcon, ViewOnEtherscanButton } from '@/shared/ui';

type BorrowDetailsSummarySectionProps = {
    assetName: string;
    walletAddress: string;
    marginAccount: string;
    riskFactor: string;
    apy: string;
    accumulatedInterest: string;
    marginCallThreshold: string | null;
    liquidationThreshold: string | null;
};

export const BorrowDetailsSummarySection: FC<BorrowDetailsSummarySectionProps> = ({
    assetName,
    marginAccount,
    riskFactor,
    apy,
    accumulatedInterest,
    marginCallThreshold,
    liquidationThreshold,
}) => {
    const classes = useBorrowDetailsSummarySectionStyle();

    const { userBorrowsOpened } = useSelector((state: Store) => ({
        userBorrowsOpened: state.borrows.userBorrowsOpened,
    }));

    const handleCopyAddress = () => {
        copyToClipboard(marginAccount);
        toast({
            title: 'Copied to clipboard!',
            variant: 'success',
        });
    };

    const tooltipAccountValue = (
        <div className={classes.tooltipAccountValue}>
            <p className={classes.titleTooltip}>Maintenance Margin</p>
            <p>
                Evaluate how your account performs under tough market conditions to ensure it stays
                safe and stable.
            </p>
        </div>
    );

    const stressTestValue = useMemo(() => {
        const userBorrowsOpenedByMarginAccount = userBorrowsOpened.find(
            (item: UserBorrow) => item.address === marginAccount,
        );

        const decimals = userBorrowsOpenedByMarginAccount?.leverage?.decimals;

        const lastBorrowItem = userBorrowsOpenedByMarginAccount?.history?.length
            ? userBorrowsOpenedByMarginAccount?.history.length - 1
            : 0;

        const lastBorrow = userBorrowsOpenedByMarginAccount?.history?.[
            lastBorrowItem
        ] as History<MaintenanceData>;

        if (!lastBorrow) {
            return 'N/A';
        }

        return Number(formatUnits(lastBorrow?.data?.stressTested, decimals))
            .toFixed(Math.min(decimals!, 8))
            .replace(/\.?0+$/, '');
    }, [userBorrowsOpened]);

    return (
        <div className={classes.root}>
            <div className={classes.title}>Summary</div>
            <div className={classes.assetWrapper}>
                <div className={classes.assetWithIconWrapper}>
                    <AssetIcon assetName={assetName} className={classes.assetIcon} />
                    <div className={classes.assetNameWrapper}>
                        <div className={classes.assetName}>{assetName}</div>
                        <Button
                            type='text'
                            className={classes.walletAddress}
                            onClick={handleCopyAddress}
                        >
                            <div>
                                {truncate({
                                    value: marginAccount || '',
                                    startSymbols: 4,
                                    endSymbols: 4,
                                })}
                            </div>
                            <CopyIcon />
                        </Button>
                    </div>
                </div>
                <ViewOnEtherscanButton resource={{ address: marginAccount }} />
            </div>
            <div className={classes.riskFactorWrapper}>
                <div className={classes.titleWithQuestion}>
                    <RiskFactorTooltip riskFactor={riskFactor} />
                </div>
                <RiskFactor
                    riskFactor={
                        +Number(riskFactor)
                            .toFixed(8)
                            .replace(/\.?0+$/, '')
                    }
                />
            </div>
            <div className={classes.riskFactorBarWrapper}>
                <RiskFactorBar
                    health={
                        +Number(riskFactor)
                            .toFixed(8)
                            .replace(/\.?0+$/, '')
                    }
                    marginCallThreshold={marginCallThreshold}
                    liquidationThreshold={liquidationThreshold}
                />
            </div>
            <div className={classes.valueWrapper}>
                <div className={classes.titleWithQuestion}>
                    <Tooltip
                        rootClassName={classes.tooltipRoot}
                        title={tooltipAccountValue}
                        placement='bottomLeft'
                        arrow={{ pointAtCenter: true }}
                    >
                        <QuestionIcon />
                    </Tooltip>
                    <div>Maintenance Margin</div>
                </div>
                <div
                    className={classes.value}
                >{`${formatWithCommas(stressTestValue)} ${assetName}`}</div>
            </div>
            <div className={classes.valueWrapper}>
                <div className={classes.titleValue}>Borrow Rate</div>
                {Number(apy) ? (
                    <div className={classes.value}>{apy}%</div>
                ) : (
                    <SkeletonInput className={classes.smallSkeleton} size='small' />
                )}
            </div>
            <div className={classes.valueWrapper}>
                <div className={classes.titleValue}>Accumulated Interest</div>
                <div className={classes.value}>
                    {Number(accumulatedInterest)
                        .toFixed(8)
                        .replace(/\.?0+$/, '')}
                </div>
            </div>
        </div>
    );
};
